import { DestroyRef, Injectable, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, filter, firstValueFrom, map } from 'rxjs';
import { apiErrorSnackKey, readApiError } from 'app/core/api-error';
import { SnackBarService } from 'app/core/snack-bar.service';
import { CommunityNotificationsService } from '../services/community-notifications.service';
import { NOTIFICATION_LIMITS } from '../services/models/community-limits';
import { bytesToBase64Url } from './vapid-key';
import {
  CLICK_MESSAGE,
  ClickMessage,
  PUSH_MESSAGE,
  PushHint,
  PushMessage,
  SUBSCRIPTION_CHANGE_MESSAGE,
  SubscriptionChangeMessage,
  WebPushState,
  WorkerMessage,
} from './web-push.models';
import { PushApi } from './push-api';

/**
 * The browser's half of web push: whether it can be offered, whether it is on, and turning it either way.
 *
 * One instance for the session, or the bell and the page each probe their own and disagree. LayoutModule is
 * eager, so registering it there is what puts it in the root injector.
 */
@Injectable()
export class WebPushService {
  private readonly api = inject(PushApi);
  private readonly notifications = inject(CommunityNotificationsService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly stateValue = signal<WebPushState>('probing');
  private readonly pendingValue = signal(false);
  private readonly pushedSubject = new Subject<PushHint>();
  private readonly clickedSubject = new Subject<string>();

  private publicKey = '';
  /** Required rather than defensive: the top bar is destroyed and rebuilt on every fullscreen navigation. */
  private hasSynced = false;

  public readonly state: Signal<WebPushState> = this.stateValue.asReadonly();
  public readonly pending: Signal<boolean> = this.pendingValue.asReadonly();
  public readonly canEnable = computed(() => this.stateValue() === 'promptRequired' || this.stateValue() === 'off');
  public readonly canDisable = computed(() => this.stateValue() === 'on');
  public readonly offerable = computed(() => this.canEnable() || this.canDisable() || this.stateValue() === 'blocked');

  public readonly pushed$: Observable<PushHint> = this.pushedSubject.asObservable();
  public readonly clicked$: Observable<string> = this.clickedSubject.asObservable();

  public syncOnAppLoad(): void {
    if (this.hasSynced) {
      return;
    }
    this.hasSynced = true;
    void this.probeAndSubscribe();
  }

  /** Re-reads the browser's own state without writing anything, for when the panel opens. */
  public async refresh(): Promise<void> {
    if (this.stateValue() === 'unsupported' || this.stateValue() === 'insecureContext') {
      return;
    }
    if (!this.publicKey) {
      return;
    }

    const permission = this.api.permission;
    if (permission === 'denied') {
      this.stateValue.set('blocked');
      return;
    }
    if (permission === 'default') {
      this.stateValue.set('promptRequired');
      return;
    }

    const subscription = await this.api.getSubscription();
    this.stateValue.set(subscription && this.matchesCurrentKey(subscription) ? this.stateValue() : 'off');
  }

  public async enable(): Promise<void> {
    this.pendingValue.set(true);
    try {
      // Before any network await: it is the only way to tell a dismissed prompt from a blocked site, and it
      // keeps the user-gesture chain WebKit requires.
      const permission = await this.api.requestPermission();
      if (permission === 'denied') {
        this.stateValue.set('blocked');
        return;
      }
      if (permission !== 'granted') {
        this.stateValue.set('promptRequired');
        return;
      }

      await this.api.register();
      await this.registerSubscription();
    } catch (error: unknown) {
      this.stateValue.set(this.blockedByBrowser(error) ? 'blocked' : 'off');
      this.report(error, 'knowledge.notifications.push.enable-failed');
    } finally {
      this.pendingValue.set(false);
    }
  }

  public async disable(): Promise<void> {
    this.pendingValue.set(true);
    try {
      const subscription = await this.api.getSubscription();
      if (!subscription) {
        this.stateValue.set('off');
        return;
      }

      // Captured before unsubscribe(): after it the object is dead and the endpoint is the only name the
      // stored row has.
      const endpoint = subscription.endpoint;
      await this.api.unsubscribe(subscription);
      this.stateValue.set('off');

      // Browser first: syncOnAppLoad re-posts any live subscription, so server-first plus a failed browser
      // unsubscribe would turn push back on at the next page load. The 410 prune makes this call tidiness.
      await firstValueFrom(this.notifications.unsubscribe(endpoint));
    } catch (error: unknown) {
      this.report(error, 'knowledge.notifications.push.disable-failed');
    } finally {
      this.pendingValue.set(false);
    }
  }

  private async probeAndSubscribe(): Promise<void> {
    if (!this.api.supported) {
      this.stateValue.set('unsupported');
      return;
    }
    if (!this.api.secureContext) {
      this.stateValue.set('insecureContext');
      return;
    }

    // Fetched before the permission branch on two counts: enable() must not await the network between the
    // click and requestPermission(), and an environment with no key must never render an enable button.
    const key = await this.readPublicKey();
    if (!key) {
      this.stateValue.set('notConfigured');
      return;
    }
    this.publicKey = key;

    try {
      await this.api.register();
      this.listenToWorker();
    } catch {
      // Silent: nothing on screen asked for this. enable() reports, the probe does not.
      this.stateValue.set('off');
      return;
    }

    const permission = this.api.permission;
    if (permission === 'denied') {
      this.stateValue.set('blocked');
      return;
    }
    if (permission === 'default') {
      this.stateValue.set('promptRequired');
      return;
    }

    try {
      await this.registerSubscription();
    } catch {
      // Silent: nothing on screen asked for this, and the next page load tries again.
      this.stateValue.set('off');
    }
  }

  private async readPublicKey(): Promise<string> {
    try {
      const answer = await firstValueFrom(this.notifications.getVapidPublicKey());
      return answer?.publicKey ?? '';
    } catch {
      return '';
    }
  }

  /** Permission is already granted on every path that reaches here, so subscribe() cannot prompt. */
  private async registerSubscription(): Promise<void> {
    let subscription = await this.api.getSubscription();

    if (subscription && !this.matchesCurrentKey(subscription)) {
      // Rotated pair. Left alone the push service answers 403, and a 403 is retained rather than pruned, so
      // nothing would ever heal it.
      const stale = subscription.endpoint;
      await this.api.unsubscribe(subscription);
      await firstValueFrom(this.notifications.unsubscribe(stale));
      subscription = null;
    }

    if (!subscription) {
      subscription = await this.api.subscribe(this.publicKey);
    }

    const p256dh = bytesToBase64Url(subscription.getKey('p256dh'));
    const auth = bytesToBase64Url(subscription.getKey('auth'));
    if (!p256dh || !auth) {
      // A subscription with no key material cannot be pushed to, and the server would refuse it anyway.
      this.stateValue.set('off');
      return;
    }

    await firstValueFrom(
      this.notifications.subscribe({
        // Flattened. PushSubscription.toJSON() nests these under `keys`, and that shape answers 204 while
        // storing nothing, so the push simply never arrives.
        endpoint: subscription.endpoint,
        p256dh,
        auth,
        ...this.userAgent(),
      }),
    );

    this.stateValue.set('on');
  }

  /** Sliced here because the server answers 400 for the whole subscribe rather than truncating. */
  private userAgent(): { userAgent?: string } {
    const value = navigator.userAgent?.slice(0, NOTIFICATION_LIMITS.maxUserAgentCharacters) ?? '';
    return value ? { userAgent: value } : {};
  }

  private matchesCurrentKey(subscription: PushSubscription): boolean {
    return bytesToBase64Url(subscription.options.applicationServerKey) === this.publicKey;
  }

  private listenToWorker(): void {
    const messages = this.api.messages().pipe(
      map((data) => data as WorkerMessage),
      filter((message) => !!message?.type),
      takeUntilDestroyed(this.destroyRef),
    );

    messages.subscribe((message) => {
      if (message.type === PUSH_MESSAGE) {
        const push = message as PushMessage;
        this.pushedSubject.next({ eventId: push.eventId, notificationType: push.notificationType });
        return;
      }
      if (message.type === CLICK_MESSAGE) {
        this.clickedSubject.next((message as ClickMessage).eventId);
        return;
      }
      if (message.type === SUBSCRIPTION_CHANGE_MESSAGE) {
        void this.onSubscriptionChanged((message as SubscriptionChangeMessage).oldEndpoint);
      }
    });
  }

  /**
   * The worker cannot post this itself: it has no gateway base url and no access to localStorage for the
   * target-site header. What it can hand over is the old endpoint, unreadable one instruction later.
   */
  private async onSubscriptionChanged(oldEndpoint: string): Promise<void> {
    try {
      if (oldEndpoint) {
        await firstValueFrom(this.notifications.unsubscribe(oldEndpoint));
      }
      await this.registerSubscription();
    } catch {
      this.stateValue.set('off');
    }
  }

  private blockedByBrowser(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'NotAllowedError';
  }

  /** An HTTP failure says what actually happened; a browser refusal only has our own sentence. */
  private report(error: unknown, fallbackKey: string): void {
    const apiError = readApiError(error);
    this.snackBarService.error(apiError.kind === 'unknown' ? fallbackKey : apiErrorSnackKey(apiError));
  }
}
